<template>
  <div class="mobile-generator">
    <!-- 整体容器：深色圆角卡片 -->
    <div class="mobile-generator-card">
      <div class="mobile-generator-inner">
        <!-- Tab 切换栏 -->
        <div class="mobile-tabs">
          <div v-for="(tab, index) in computedTabs" :key="index" class="mobile-tab-item"
            :class="{ active: activeTab === tab.value }" @click="setActiveTab(tab)">
            <span :class="logoEnums[tab.value as keyof typeof logoEnums]" class="w-4 h-4"></span>
            {{ tab.name }}
          </div>
        </div>

        <!-- 模型选择器 -->
        <div class="mobile-model-selector">
          <tool-new-model-select v-model:model="selectedModelKey" :model_list="currentModelList"
            :type="currentModelType" :style_class="model_select_style" :hideLabel="true"
            popper_style="mobile_popper_style"></tool-new-model-select>
        </div>

        <!-- 内容区域 -->
        <div class="mobile-content">
          <!-- 图生图/图生视频 Tab -->
          <div v-if="activeTab === 'image'" class="mobile-tab-panel">
            <div class="mobile-upload-area">
              <NewModelPageImageUpload :max-size-m-b="10" :max-resolution="4096" :allow-multiple="false"
                :show-hint="true" accept-types="image/jpeg,image/png,image/webp" @upload-success="onImageUploadSuccess"
                @upload-error="onImageUploadError" @files-change="onImageFilesChange" />
            </div>
          </div>

          <!-- 文生图/文生视频 Tab -->
          <div v-if="activeTab === 'text'" class="mobile-tab-panel">
            <div class="mobile-text-area">
              <textarea v-model="promptText" class="mobile-textarea" :placeholder="t('menus.promptPlaceholder')"
                rows="4" maxlength="2000"></textarea>
            </div>
          </div>
        </div>

        <!-- 底部按钮 -->
        <div class="mobile-action">
          <button v-if="isLoggedIn" class="mobile-generate-btn"
            :class="{ disabled: !canGenerate, loading: isGenerating }" :disabled="!canGenerate || isGenerating"
            @click="handleGenerate">
            <div class="btn-icon">
              <img src="~/assets/images/common/create_btn.svg" alt="Create button icon" class="w-6 h-6" />
            </div>
            <span class="btn-text">
              {{ isGenerating ? t('menus.creating') : t('menus.create') }}
            </span>
          </button>

          <!-- 未登录按钮 -->
          <div v-else-if="LOGIN_ENTRY_ENABLED" class="mobile-login-btn" @click="handleTryNow">
            <img src="https://static.weryai.com/growth/image/weryai_ce29b49010526e7fa8e91d971a726de4.webp"
              alt="微鲤AI logo" class="w-5 h-5" />
            <span>{{ t('common.loginToGetFree') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 灵感推荐 - 放在整个卡片下面 -->
    <div v-if="showInspirationRecommend" class="mobile-inspiration">
      <InspirationRecommend 
        :model-key="props.modelKey"
        :filter-type="currentTabType"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { LOGIN_ENTRY_ENABLED } from '~/utils/feature-flags'
import { ref, computed, onMounted, watch } from 'vue'
import { navigateTo } from '#app'
import NewModelPageImageUpload from '~/components/NewModelPageImageUpload.vue'
import ToolNewModelSelect from '~/components/Tool/NewModelSelect.vue'
import InspirationRecommend from '~/components/InspirationRecommend.vue'
import { useI18n } from '~/composables/useI18n'
import { analyticsEvent } from '~/utils/log-event-func'
import { useToolsHooks } from '@/utils/useHooks'
import { useAppStore } from '~/stores/app'
import { formatAndGroupData } from '~/utils/common'
import { showLoginModal } from '~/utils/emitter'
import { useUserStore } from '~/stores/user'

/**
 * MobileGenerator - 移动端通用生成器组件
 * 支持 video（视频生成）和 image（图片生成）两种模式
 */

// Props 定义
interface Props {
  /** 生成器类型：video 或 image */
  type?: 'video' | 'image'
  /** 模型名称 */
  modelName?: string
  /** 模型版本 */
  modelVersion?: string
  /** 模型 Key */
  modelKey?: string
  /** 默认激活的 Tab */
  defaultTab?: 'image' | 'text'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'image',
  modelName: '',
  modelVersion: '',
  modelKey: '',
  defaultTab: 'image',
})

// Emits - 定义组件需要发送的事件
const emit = defineEmits<{
  'tab-change': [tabType: string]
}>()

// Composables
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const { t } = useI18n()
const { handleMenuLinkClick, imageModelsEnums, videoModelsEnums } = useToolsHooks()
const appStore = useAppStore()
const modelData = computed(() => appStore.modelData)

// 根据 modelKey 匹配当前模型的元数据配置
const currentModelConfig = computed(() => {
  const enums =
    props.type === 'video' ? videoModelsEnums.value : imageModelsEnums.value
  const key = selectedModelKey.value || props.modelKey
  return enums.find(m => m.model_key === key)
})

// 响应式状态
const activeTab = ref<'image' | 'text'>(props.defaultTab)
const promptText = ref('')
const uploadedImage = ref<File | null>(null)
const selectedModelKey = ref('')
const isGenerating = ref(false)

// 计算 Tabs
const computedTabs = computed(() => {
  const allTabs =
    props.type === 'video'
      ? [
        {
          name: t('menus.createModal.textToVideo'),
          value: 'text',
          type: 'text-to-video',
        },
        {
          name: t('menus.createModal.imageToVideo'),
          value: 'image',
          type: 'image-to-video',
        },
      ]
      : [
        {
          name: t('menus.createModal.textToImage'),
          value: 'text',
          type: 'text-to-image',
        },
        {
          name: t('menus.createModal.imageToImage'),
          value: 'image',
          type: 'image-to-image',
        },
      ]

  // 如果在 useHooks 中找到了该模型且定义了支持类型，则进行过滤
  const config = currentModelConfig.value
  if (config?.supportType) {
    return allTabs.filter(tab =>
      config.supportType.includes(tab.type)
    )
  }

  return allTabs
})

// 自动纠正 activeTab，确保当前选择的 Tab 在支持范围内
watch(
  () => computedTabs.value,
  newTabs => {
    if (newTabs.length > 0) {
      const supportedValues = newTabs.map(t => t.value)
      // 如果当前 activeTab 不在支持列表中
      if (!supportedValues.includes(activeTab.value)) {
        // 自动选择第一个支持的 Tab
        activeTab.value = supportedValues[0] as 'text' | 'image'
      }
    }
  },
  { immediate: true }
)

// 当前模型类型
const currentModelType = computed(() => {
  if (props.type === 'video') {
    return activeTab.value === 'image' ? 'image_to_video' : 'text_to_video'
  } else {
    return activeTab.value === 'image' ? 'image_to_image' : 'text_to_image'
  }
})

// 当前标签页对应的类型(用于InspirationRecommend组件)
const currentTabType = computed(() => {
  return activeTab.value === 'image' ? 'IMG_TO_VIDEO' : 'TEXT_TO_VIDEO'
})

// 当前模型列表
const currentModelList = computed(() => {
  if (!modelData.value) return []

  const modelCategory = props.type === 'video' ? 'video_models' : 'image_models'
  const models = modelData.value?.[modelCategory]?.[currentModelType.value] || []
  return formatAndGroupData(models)
})

// 当前选中的模型对象
const currentSelectedModel = computed(() => {
  if (!selectedModelKey.value || !currentModelList.value) return null
  return currentModelList.value.find(
    model => model.model_key === selectedModelKey.value
  ) || null
})

// 按钮可用状态
const canGenerate = computed(() => {
  if (activeTab.value === 'text') {
    return promptText.value.trim().length > 0
  }
  return uploadedImage.value !== null
})

// 判断是否显示灵感推荐
// 只有 Veo3.1、Veo3、Sora2、Runway 这些模型才显示灵感推荐
const showInspirationRecommend = computed(() => {
  const supportedModels = ['sora', 'google-veo3-1', 'google-veo3', 'RUNWAY']
  return supportedModels.includes(props.modelKey) && props.type === 'video'
})

// Tab icon 枚举
const logoEnums = {
  image: 'i-edit-pic-icon',
  text: 'i-pen-icon',
}

// 切换 Tab
const setActiveTab = (tab: { name: string; value: string }) => {
  activeTab.value = tab.value as 'image' | 'text'
  // 通知父组件标签页变化,用于CommunityWorks组件过滤
  emit('tab-change', currentTabType.value)
}

// 自动匹配并选择默认模型
watch(
  [() => currentModelList.value, () => activeTab.value],
  ([newModelList]) => {
    if (newModelList?.length > 0) {
      if (
        !selectedModelKey.value ||
        !newModelList.some((m: any) => m.model_key === selectedModelKey.value)
      ) {
        let matchedModel = null

        // 优先使用 modelKey prop 进行精确匹配
        if (props.modelKey) {
          matchedModel = newModelList.find(
            (m: any) => m.model_key === props.modelKey
          )
        }

        // 如果 modelKey 没有匹配，尝试根据 modelName 匹配
        if (!matchedModel && props.modelName) {
          const modelNameLower = props.modelName.toLowerCase().trim()
          matchedModel = newModelList.find((m: any) => {
            const titleLower = (m.title || '').toLowerCase().trim()
            const keyLower = (m.model_key || '').toLowerCase().trim()
            return (
              titleLower === modelNameLower ||
              keyLower === modelNameLower ||
              titleLower.includes(modelNameLower) ||
              modelNameLower.includes(titleLower)
            )
          })
        }

        selectedModelKey.value =
          matchedModel?.model_key || newModelList[0]?.model_key || ''
      }
    }
  },
  { immediate: true }
)

// 处理生成
const handleGenerate = () => {
  if (!canGenerate.value) return

  const is_client = import.meta.client
  if (!is_client) return

  // 文生图/文生视频
  if (activeTab.value === 'text') {
    const prompt = promptText.value.trim()
    if (prompt) {
      localStorage.setItem('modelPagePrompt', prompt)
      localStorage.setItem('modelPageModelKey', selectedModelKey.value || '')

      if (props.modelVersion) {
        localStorage.setItem('modelPageModelVersion', props.modelVersion)
      }

      analyticsEvent('click_model_page_common_create', {
        model_name: selectedModelKey.value,
        type: props.type,
      })

      const selectedModel = currentSelectedModel.value
      const encodedPrompt = encodeURIComponent(prompt)
      const targetPath = props.type === 'video' ? 'text-to-video' : 'text-to-image'

      if (selectedModel?.supportType && selectedModel.model_key) {
        const path = `/${selectedModel.supportType[0]}?defaultModel=${selectedModel.model_key}${selectedModel.version ? '&version=' + selectedModel.version : ''}&customType=modelPage&prompt=${encodedPrompt}&selectedModel=${selectedModelKey.value}`
        navigateTo(path)
      } else {
        handleMenuLinkClick(
          `/${targetPath}?defaultModel=${props.modelKey}${props.modelVersion ? '&version=' + props.modelVersion : ''}&customType=modelPage&prompt=${encodedPrompt}&selectedModel=${selectedModelKey.value}`
        )
      }
    }
  }
}

// 图片上传成功
const onImageUploadSuccess = (files: any[]) => {
  if (files.length > 0 && import.meta.client) {
    const uploadResult = files[0].uploadResult
    const imageUrl =
      uploadResult?.data?.object_url_list?.[0] ||
      uploadResult?.data?.url ||
      uploadResult?.url ||
      files[0].url

    if (imageUrl) {
      isGenerating.value = true
      localStorage.setItem('modelPageFileUrl', imageUrl)
      localStorage.setItem('modelPageModelKey', selectedModelKey.value || '')

      if (props.modelVersion) {
        localStorage.setItem('modelPageModelVersion', props.modelVersion)
      }

      analyticsEvent('click_model_page_common_create', {
        model_name: selectedModelKey.value,
        type: props.type,
      })

      const selectedModel = currentSelectedModel.value
      const encodedImageUrl = encodeURIComponent(imageUrl)
      const targetPath = props.type === 'video' ? 'image-to-video' : 'image-to-image'

      if (selectedModel?.supportType && selectedModel.model_key) {
        const path = `/${selectedModel.supportType[0]}?defaultModel=${selectedModel.model_key}${selectedModel.version ? '&version=' + selectedModel.version : ''}&customType=modelPage&imageUrl=${encodedImageUrl}&selectedModel=${selectedModelKey.value}`
        navigateTo(path)
      } else {
        handleMenuLinkClick(
          `/${targetPath}?defaultModel=${props.modelKey}${props.modelVersion ? '&version=' + props.modelVersion : ''}&customType=modelPage&imageUrl=${encodedImageUrl}&selectedModel=${selectedModelKey.value}`
        )
      }
    }
  }
}

const onImageUploadError = (error: string) => {
  console.error('Image upload failed:', error)
  isGenerating.value = false
}

const onImageFilesChange = (files: any[]) => {
  uploadedImage.value = files.length > 0 ? files[0].file : null
}

const handleTryNow = () => {
  showLoginModal()
}

// 初始化
onMounted(() => {
  if (!modelData.value) {
    appStore.getModelList()
  }
  // 初始化时通知父组件当前标签页类型
  emit('tab-change', currentTabType.value)
})

// 模型选择器样式
const model_select_style = `
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.10);
    background: rgba(0, 0, 0, 0.40);
    -webkit-backdrop-filter: blur(6px);
    backdrop-filter: blur(6px);
`
</script>

<style lang="less" scoped>
.mobile-generator {
  width: 100%;
}

.mobile-generator-card {
  padding: 4px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.10);
  background: rgba(255, 255, 255, 0.05);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(6px);
}

.mobile-generator-inner {
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(13, 14, 18, 0.80);
}

/* Tab 切换栏 */
.mobile-tabs {
  display: flex;
  width: 100%;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.4);
  padding: 4px;
  gap: 0;
}

.mobile-tab-item {
  flex: 1;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  cursor: pointer;
  border-radius: 9999px;
  transition: all 0.3s ease;
  border: 1px solid transparent;

  &.active {
    border: 1px solid rgba(153, 247, 104, 0.30);
    background: rgba(255, 255, 255, 0.10);
    color: #99F768;
  }
}

/* 模型选择器 */
.mobile-model-selector {
  margin-top: 16px;
  width: 100%;
}

/* 内容区域 */
.mobile-content {
  margin-top: 16px;
}

.mobile-tab-panel {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 上传区域 */
.mobile-upload-area {
  width: 100%;

  /* 覆盖上传组件样式 - 与文字输入框保持一致 */
  :deep(.upload-area) {
    min-height: 120px;
    padding: 24px 16px;
    border-radius: 16px;
    border: 2px dashed rgba(255, 255, 255, 0.10);

    &:hover {
      border-color: rgba(153, 247, 104, 0.3);
    }
  }

  :deep(.upload-placeholder) {
    flex-direction: column;
    gap: 8px;
  }

  :deep(.upload-icon) {
    margin-bottom: 0;
  }

  :deep(.upload-text) {
    font-size: 14px;
  }

  :deep(.upload-hint-inner) {
    font-size: 12px;
    margin-top: 4px;
  }
}

/* 文本输入区域 */
.mobile-text-area {
  width: 100%;
  min-height: 120px;
  border-radius: 16px;
  border: 2px dashed rgba(255, 255, 255, 0.10);
  background: rgba(0, 0, 0, 0.2);
  padding: 16px;

  &:focus-within {
    border-color: rgba(153, 247, 104, 0.3);
  }
}

.mobile-textarea {
  width: 100%;
  min-height: 80px;
  background: transparent;
  border: none;
  outline: none !important;
  color: #fff;
  font-size: 14px;
  resize: none;
  -webkit-appearance: none;
  appearance: none;
  box-shadow: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
  }
}

/* 底部按钮 */
.mobile-action {
  margin-top: 16px;
  width: 100%;
}

/* 灵感推荐 - 在整个卡片下面 */
.mobile-inspiration {
  margin-top: 16px;
  width: 100%;
  
  /* 移动端灵感推荐样式调整 */
  :deep(.inspiration-recommend) {
    min-height: 280px;
    border-radius: 16px;
    border: 2px solid rgba(255, 255, 255, 0.08);
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(25px);
  }
  
  :deep(.inspiration-header) {
    padding: 12px;
  }
  
  :deep(.inspiration-list) {
    padding: 12px;
    gap: 12px;
  }
  
  :deep(.inspiration-item) {
    padding: 12px;
    border-radius: 12px;
  }
  
  :deep(.inspiration-footer) {
    padding: 12px;
  }
  
  :deep(.view-more-btn) {
    max-width: 100%;
    height: 44px;
  }
}

.mobile-generate-btn {
  width: 100%;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #99F768;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 0 20px rgba(153, 247, 104, 0.4);

  .btn-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .btn-text {
    font-size: 16px;
    font-weight: 600;
    color: #080808;
  }

  &:hover:not(.disabled) {
    background: #82d945;
    transform: scale(1.02);
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.loading {
    cursor: wait;
  }
}

.mobile-login-btn {
  width: 100%;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #99F768;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 0 20px rgba(153, 247, 104, 0.4);

  span {
    font-size: 16px;
    font-weight: 600;
    color: #080808;
  }

  &:hover {
    background: #82d945;
    transform: scale(1.02);
  }
}

/* Icon 样式 */
.i-pen-icon {
  mask: url('~/assets/images/common/pen.svg') no-repeat center;
  mask-size: contain;
  -webkit-mask: url('~/assets/images/common/pen.svg') no-repeat center;
  -webkit-mask-size: contain;
  background-image: none;
  background-color: currentColor;
}

.i-edit-pic-icon {
  mask: url('~/assets/images/common/edit_pic.svg') no-repeat center;
  mask-size: contain;
  -webkit-mask: url('~/assets/images/common/edit_pic.svg') no-repeat center;
  -webkit-mask-size: contain;
  background-image: none;
  background-color: currentColor;
}
</style>

<style>
.mobile_popper_style {
  background: rgba(0, 0, 0, 0.8) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

@media (max-width: 767px) {
  .mobile_popper_style {
    background: rgba(0, 0, 0, 0.7) !important;
    -webkit-backdrop-filter: none !important;
    backdrop-filter: none !important;
  }
}

/* Fallback: for browsers/webviews that don't support backdrop-filter */
@supports not ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px))) {
  .mobile_popper_style {
    background: rgba(0, 0, 0, 0.7) !important;
  }
}
</style>
